import * as THREE from 'three';
import { getRoundPointTexture, ROUND_POINT_THRESHOLD_PIXELS } from './PointCloudRenderer';
/**
 * Renders small points as cheap square sprites and larger points as discs.
 *
 * Both passes share the source geometry. The source Points object is the square
 * pass; a child Points object is the round pass, so file transforms, alignment,
 * visibility, and removal are inherited without maintaining proxy transforms.
 * Each vertex reaches rasterization in exactly one pass.
 */
export class AdaptivePointRenderer {
    constructor(host) {
        this.host = host;
        this.views = new Map();
    }
    beforeRender() {
        // The size-band hook below is GLSL/WebGL-specific. WebGPU remains an opt-in
        // backend and keeps its ordinary point rendering rather than double-drawing.
        if (this.host.rendererBackend !== 'webgl') {
            this.removeAllViews();
            return;
        }
        const sources = new Set(this.pointClouds());
        for (const [source, view] of this.views) {
            if (!sources.has(source) || source.material !== view.sourceMaterial) {
                this.removeView(source, view);
            }
        }
        for (const source of sources) {
            let view = this.views.get(source);
            if (!view) {
                view = this.createView(source);
                this.views.set(source, view);
            }
            this.syncView(source, view);
        }
    }
    pointClouds() {
        return this.host.meshes.filter((mesh) => mesh instanceof THREE.Points && mesh.material instanceof THREE.PointsMaterial);
    }
    createView(source) {
        const sourceMaterial = source.material;
        // The material's *own* hooks, never a band wrapper this class installed
        // earlier. A material can reach here twice — a depth reconversion assigns
        // the new material to the existing mesh, which builds a view, and then
        // hands the same material to the replacement mesh, which builds another —
        // and capturing the wrapper as "inherited" made the second round pass
        // inherit the square pass's rejection on top of its own. The two are
        // complementary, so between them they rejected every point: everything
        // above the pixel threshold was drawn by nobody, and raising the point
        // size only pushed more points over it.
        const { compile: inheritedCompile, cacheKey: inheritedCacheKey } = originalHooks(sourceMaterial);
        const roundMaterial = sourceMaterial.clone();
        configurePointBand(sourceMaterial, 'square', inheritedCompile, inheritedCacheKey);
        configurePointBand(roundMaterial, 'round', inheritedCompile, inheritedCacheKey);
        const roundPoints = new THREE.Points(source.geometry, roundMaterial);
        roundPoints.name = 'adaptive-round-pass';
        roundPoints.frustumCulled = source.frustumCulled;
        source.add(roundPoints);
        return {
            sourceMaterial,
            sourceMaterialVersion: sourceMaterial.version,
            roundPoints,
            roundMaterial,
        };
    }
    syncView(source, view) {
        const square = view.sourceMaterial;
        const round = view.roundMaterial;
        view.roundPoints.geometry = source.geometry;
        view.roundPoints.frustumCulled = source.frustumCulled;
        round.size = square.size;
        round.color.copy(square.color);
        round.opacity = square.opacity;
        // `needsUpdate` increments Material.version. Mirror that invalidation so
        // shader-affecting changes such as the sRGB decode mode reach both passes.
        let requiresUpdate = view.sourceMaterialVersion !== square.version;
        view.sourceMaterialVersion = square.version;
        const sync = (key) => {
            if (round[key] !== square[key]) {
                round[key] = square[key];
                requiresUpdate = true;
            }
        };
        sync('transparent');
        sync('blending');
        sync('depthTest');
        sync('depthWrite');
        sync('depthFunc');
        sync('colorWrite');
        sync('fog');
        sync('vertexColors');
        sync('sizeAttenuation');
        sync('toneMapped');
        sync('premultipliedAlpha');
        sync('dithering');
        sync('alphaHash');
        sync('alphaToCoverage');
        if (requiresUpdate) {
            round.needsUpdate = true;
        }
    }
    removeView(source, view) {
        source.remove(view.roundPoints);
        view.roundMaterial.dispose();
        this.views.delete(source);
    }
    removeAllViews() {
        for (const [source, view] of this.views) {
            this.removeView(source, view);
        }
    }
}
/**
 * The pristine `onBeforeCompile` / `customProgramCacheKey` for a material.
 *
 * Stashed on first sight and returned unchanged afterwards, so configuring a
 * band is idempotent however many times a material passes through a view.
 */
const PRISTINE_HOOKS = new WeakMap();
function originalHooks(material) {
    const existing = PRISTINE_HOOKS.get(material);
    if (existing) {
        return existing;
    }
    const hooks = {
        compile: material.onBeforeCompile,
        cacheKey: material.customProgramCacheKey.bind(material),
    };
    PRISTINE_HOOKS.set(material, hooks);
    return hooks;
}
function configurePointBand(material, band, inheritedCompile, inheritedCacheKey) {
    const round = band === 'round';
    material.map = null;
    material.alphaMap = round ? getRoundPointTexture() : null;
    material.alphaTest = round ? 0.5 : 0;
    material.onBeforeCompile = (shader, renderer) => {
        inheritedCompile.call(material, shader, renderer);
        const cutoff = ROUND_POINT_THRESHOLD_PIXELS.toFixed(1);
        const reject = round ? `gl_PointSize <= ${cutoff}` : `gl_PointSize > ${cutoff}`;
        shader.vertexShader = shader.vertexShader.replace('#include <logdepthbuf_vertex>', `if (${reject}) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); }\n#include <logdepthbuf_vertex>`);
    };
    material.customProgramCacheKey = () => `${inheritedCacheKey()}|point-band:${band}`;
    material.needsUpdate = true;
}
